'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { DatePickerRootContext } from '../date-picker/context/date-picker-root.context';
import { CalendarRootContext } from './context/calendar-root.context';
import { useCalendar } from './hooks/use-calendar';
import { calendarStyles } from './styles/calendar-root.styles';
import type { CalendarRootPropsInterface } from './types/calendar-root.types';

export const CalendarRoot = forwardRef<HTMLDivElement, CalendarRootPropsInterface>(
  (props: CalendarRootPropsInterface, forwardedRef) => {
    const datePickerContext = useContext(DatePickerRootContext);
    const { datePicker } = datePickerContext;
    const {
      children,
      css: cssProp,
      className,
      minDate = datePickerContext.minDate,
      maxDate = datePickerContext.maxDate,
      disabledDates = datePickerContext.disabledDates,
      dateFormat = datePickerContext.dateFormat,
      selected,
      defaultSelected,
      'aria-label': ariaLabel = 'Choose date',
      role = 'region',
      onSelectedChange,
      ...rest
    } = props;

    const hasSelected = 'selected' in props;
    const hasDefaultSelected = 'defaultSelected' in props;
    const datePickerValue = datePicker?.pendingValue ?? datePicker?.value;
    const resolvedSelected = hasSelected ? selected : datePickerValue;

    const calendar = useCalendar({
      minDate,
      maxDate,
      disabledDates,
      dateFormat,
      ...(hasSelected || datePicker ? { selected: resolvedSelected } : {}),
      ...(hasDefaultSelected ? { defaultSelected } : {}),
      onSelectedChange,
      onValueChange: datePicker?.onValueChange,
      ...rest,
    });

    const styles = css(calendarStyles, cssProp);

    const contextValue = useMemo(() => ({ calendar }), [calendar]);

    return (
      <CalendarRootContext.Provider value={contextValue}>
        <div
          ref={forwardedRef}
          data-c11n-component="Calendar.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_calendar__root', styles, className)}
          aria-label={ariaLabel}
          role={role}
          {...rest}
        >
          {children}
        </div>
      </CalendarRootContext.Provider>
    );
  },
);

CalendarRoot.displayName = 'Calendar.Root';
